import { UI } from "../constants/tokens";
import { timeAgoMs } from "../utils/timeAgo";

// Freshness of the geopolitical scanner, stated as a fact with a timestamp.
//
// The only input is max(created_at) over geo_events — see fetchLastScan in
// utils/geoEvents.js. Every scanned article becomes a row (including the ones
// classified 'none'), so that timestamp is when the scanner last ran, not when
// something interesting last happened.
//
// Three states, and no fourth: a missing timestamp is shown as missing. There
// is deliberately no default age, no "recently", no optimistic fallback — if
// the table is empty or unreachable the indicator says so.
//
// Colour: the neutral ramp only. A live indicator is a status, and VISION.md
// reserves green and red for a number moving up or down. Freshness is carried
// by lightness (secondary → muted → hairline) plus the pulse, not by hue.

// Matches LOOKBACK_H in netlify/functions/geo-classify-scheduled.js: the
// scanner only considers articles from the last 6 hours, so a gap shorter than
// that window cannot indicate a missed run. The schedule is hourly, but a run
// that finds no new articles writes no row — a 2h threshold would paint a
// healthy system as broken.
const FRESH_WINDOW_MS = 6 * 60 * 60 * 1000;

export function scanState(lastScan) {
  if (!Number.isFinite(lastScan)) return "unknown";
  return Date.now() - lastScan < FRESH_WINDOW_MS ? "fresh" : "stale";
}

const STATE = {
  fresh:   { color: UI.text.secondary, pulse: true,  title: "הסורק פעל בשש השעות האחרונות" },
  stale:   { color: UI.text.muted,     pulse: false, title: "הסורק לא פעל בשש השעות האחרונות" },
  unknown: { color: UI.line.hairline,  pulse: false, title: "אין נתון על מועד הסריקה" },
};

export default function ScanStatusDot({ lastScan, withLabel = false, size = 6 }) {
  const state = scanState(lastScan);
  const { color, pulse, title } = STATE[state];

  const dot = (
    <span
      className={pulse ? "live-dot" : undefined}
      title={title}
      style={{ width: size, height: size, borderRadius: "50%", background: color, display: "inline-block", flexShrink: 0 }}
    />
  );

  if (!withLabel) return dot;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: UI.text.muted }}>
      {dot}
      <span>
        {state === "unknown"
          ? "מועד הסריקה אינו זמין"
          : <>נסרק לאחרונה · {timeAgoMs(lastScan)}</>}
      </span>
    </div>
  );
}
